import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

// Escala azul de marca (tokens.css / tokens.json).
// PrimeNG espera escala 50..950; mapeamos la escala 100..900 del manual
// y derivamos 50 y 950 por extensión coherente (más claro / más oscuro).
export const symBlue = {
  50:  '#F4F8FE',
  100: '#ECF2FE',
  200: '#CFE0FB',
  300: '#A9C4F5',
  400: '#7BA3EF',
  500: '#5B87E0',
  600: '#3B6FE8', // PRIMARY · color de marca
  700: '#2F55C9',
  800: '#244A8F',
  900: '#1B3A73', // navy 900
  950: '#142C57',
};

// Neutros de marca como "surface" (gris azulado frío del manual).
export const symSurface = {
  0:   '#FFFFFF',
  50:  '#F4F6FA', // --sym-surface / gray-100
  100: '#ECEFF5',
  200: '#E8EBF1', // --sym-border
  300: '#C9CED8', // --sym-gray-300
  400: '#9AA3B2',
  500: '#6B7280', // --sym-gray-500
  600: '#4B5563',
  700: '#3F4654', // --sym-gray-700
  800: '#1F2633',
  900: '#141414', // --sym-ink
  950: '#0B1626', // fondo dark
};

export const Sym70Preset = definePreset(Aura, {
  primitive: {
    blue: symBlue,
    // colores semánticos del manual (se referencian abajo)
    green: { 500: '#1F7A4D', 600: '#1A6A43' },
    amber: { 500: '#9A6A00', 600: '#855B00' },
    red:   { 500: '#B23A28', 600: '#9C3122' },
    borderRadius: { sm: '6px', md: '9px', lg: '12px', xl: '16px' },
  },
  semantic: {
    primary: symBlue,
    // radios de marca (botones/inputs = md 9px)
    borderRadius: { none: '0', xs: '6px', sm: '6px', md: '9px', lg: '12px', xl: '16px' },
    focusRing: { width: '3px', style: 'solid', color: '{primary.500}', offset: '2px' },
    colorScheme: {
      light: {
        primary: {
          color: '#3B6FE8', contrastColor: '#FFFFFF',
          hoverColor: '#2F55C9', activeColor: '#244A8F',
        },
        highlight: {
          background: '#ECF2FE', focusBackground: '#CFE0FB',
          color: '#2F55C9', focusColor: '#244A8F',
        },
        surface: symSurface,
        // mapear texto/fondos a tokens de marca
        content: { background: '#FFFFFF', borderColor: '#E8EBF1' },
        text: { color: '#141414', mutedColor: '#6B7280' },
        // Aura's default formField falls back to raw {surface.*} indices, which
        // resolve against symSurface above — fine by coincidence in light mode
        // (surface.300/500/700 already equal our gray tokens) but explicit here
        // so inputs/selects/textareas stay pinned to the real design tokens.
        formField: {
          background: '#FFFFFF',
          borderColor: '#E8EBF1',
          hoverBorderColor: '#C9CED8',
          color: '#3F4654',
          placeholderColor: '#6B7280',
          iconColor: '#6B7280',
        },
      },
      dark: {
        primary: {
          color: '#5B87E0', contrastColor: '#0B1626',
          hoverColor: '#7BA3EF', activeColor: '#A9C4F5',
        },
        highlight: {
          background: 'color-mix(in srgb, #5B87E0, transparent 84%)',
          focusBackground: 'color-mix(in srgb, #5B87E0, transparent 76%)',
          color: '#EAF0FB', focusColor: '#EAF0FB',
        },
        surface: symSurface,
        content: { background: '#0F1F39', borderColor: '#1C2C49' },
        text: { color: '#EAF0FB', mutedColor: '#8AA0C8' },
        // Without this, Aura's default formField.borderColor ({surface.600})
        // resolves to symSurface[600] = #4B5563 — a flat neutral gray with no
        // navy tint, clashing against the rest of the dark theme's chrome
        // (confirmed live: every pInputText in the app, including ones never
        // touched by this migration, rendered this mismatched border/white
        // text instead of --sym-border/--sym-ink dark).
        formField: {
          background: '#0B1626',
          borderColor: '#1C2C49',
          hoverBorderColor: '#244A8F',
          color: '#EAF0FB',
          placeholderColor: '#8AA0C8',
          iconColor: '#8AA0C8',
        },
      },
    },
  },
  components: {
    // El cromo de tabla (cabecera, celdas de cabecera, pie y paginador) va sobre
    // --sym-surface, no sobre el fondo de contenido. Aura por defecto los manda a
    // {content.background} = blanco en light, así que una tabla nativa quedaba con
    // cabecera blanca al lado de otra con cabecera gris cuando la otra pintaba el
    // gris a mano por plantilla (medido en Costos Directos Venta: #FFFFFF contra
    // #F4F6FA en la misma pantalla, para el mismo rol).
    //
    // Valores explícitos por esquema, no var(--sym-*), por consistencia con
    // formField/content/text más arriba: este archivo ES la capa de mapeo de
    // tokens, y el preset se compila a su propia hoja de estilos. Referenciar
    // aquí las variables de marca invertiría la dirección de la dependencia
    // (PrimeNG pasaría a depender del CSS de marca en vez de al revés).
    // Nota: los tres disparadores de tema sí están sincronizados —
    // `_app-bridge.scss` replica los valores dark bajo `html.app-dark`— así que
    // var() *funcionaría*; no se usa por convención del archivo, no por riesgo.
    datatable: {
      colorScheme: {
        light: {
          header:     { background: '#F4F6FA' },
          headerCell: { background: '#F4F6FA' },
          footerCell: { background: '#F4F6FA' },
        },
        dark: {
          header:     { background: '#0F1F39' },
          headerCell: { background: '#0F1F39' },
          footerCell: { background: '#0F1F39' },
        },
      },
    },
    treetable: {
      colorScheme: {
        light: {
          header:     { background: '#F4F6FA' },
          headerCell: { background: '#F4F6FA' },
          footerCell: { background: '#F4F6FA' },
        },
        dark: {
          header:     { background: '#0F1F39' },
          headerCell: { background: '#0F1F39' },
          footerCell: { background: '#0F1F39' },
        },
      },
    },
    paginator: {
      colorScheme: {
        light: { root: { background: '#F4F6FA' } },
        dark:  { root: { background: '#0F1F39' } },
      },
    },
    // La franja de pestañas es **cromo de página**, un escalón distinto del cromo
    // de tarjeta (barra de controles, cabecera de tabla, paginador — todos los de
    // arriba, en --sym-surface).
    //
    // Cuando los dos usaban el mismo #F4F6FA, ese gris dejaba de significar nada:
    // en Costos Directos Venta hacía cuatro trabajos distintos —franja de tabs,
    // barra de controles, cabecera de tabla y fila de filtros— y la pantalla se
    // leía como un rayado de bandas en vez de como capas. Ahora hay una rampa de
    // profundidad real: página → franja de tabs → cromo de tarjeta → dato.
    //
    // En claro la franja es más oscura que el cromo de tarjeta; en oscuro es más
    // oscura también (los temas oscuros elevan con luz, así que "más al fondo" =
    // más oscuro). En ambos casos queda entre el fondo de página y la tarjeta.
    tabs: {
      colorScheme: {
        light: { tablist: { background: '#E9EBF0' } }, // --sym-surface + 5% ink
        dark:  { tablist: { background: '#0C1A2F' } }, // entre --sym-bg y --sym-surface
      },
    },
    // Aura fija el ancho máximo del tooltip en 12.5rem, pensado para un root de
    // 16px (=200px). Aquí LayoutService fuerza el root a 11px, así que ese mismo
    // 12.5rem valía ~138px: cualquier tooltip de más de tres palabras —y en
    // Talento Humano casi todos son una frase explicativa— se convertía en una
    // columna estrecha y altísima que se sale del viewport por arriba.
    //
    // 22rem = ~242px con el root de 11px: unas 45 columnas de texto, un largo de
    // línea legible sin volverse un párrafo. Los textos de verdad largos siguen
    // teniendo `.sym-tooltip-wide` (30rem) para ensancharse aún más.
    tooltip: {
      maxWidth: '22rem',
    },
  },
});
